<template>
  <div class="w-full">
    <b-form @submit.prevent="submitExemption">
      <b-row>
        <b-col cols="12">
          <b-form-group label="Tracking Number" label-for="trackingNo">
            <b-form-input
              id="trackingNo"
              v-model="form.trackingNo"
              type="text"
              readonly
              class="form-control"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Rationale" label-for="rationale" :invalid-feedback="errors.rationale">
            <b-form-textarea
              id="rationale"
              v-model="form.rationale"
              placeholder="Please provide a rationale for the stamp duty exemption..."
              rows="4"
              max-rows="6"
              :state="errors.rationale ? false : null"
              required
            />
            <b-form-text class="text-muted">
              Maximum 255 characters ({{ form.rationale.length }}/255)
            </b-form-text>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Application Type" label-for="type" :invalid-feedback="errors.type">
            <b-form-select
              class="form-control"
              id="type"
              v-model="form.type"
              :options="applicationTypes"
              :state="errors.type ? false : null"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Supporting Document" label-for="supportingDocument" :invalid-feedback="errors.supportingDocument">
            <b-form-file
              id="supportingDocument"
              v-model="form.supportingDocument"
              placeholder="Choose a file..."
              drop-placeholder="Drop file here..."
              accept=".pdf"
              :state="errors.supportingDocument ? false : null"
              required
            />
            <b-form-text class="text-muted">
              Accepted formats: PDF
            </b-form-text>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col cols="12" class="text-right">
          <b-button type="button" variant="secondary" @click="cancel" class="mr-2">
            Cancel
          </b-button>
          <b-button 
            type="submit" 
            variant="primary" 
            :disabled="loading || !isFormValid"
            class="px-4"
          >
            <b-spinner v-if="loading" small class="mr-2"></b-spinner>
            Submit Exemption
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "StampDutyExemptionForm",
  props: {
    trackingNumber: {
      type: String,
      required: true
    },
    applicationTypes: {
      type: Array,
      default: () => [
        { value: null, text: "Please select application type" },
        { value: "STAMP_DUTY_EXEMPTION", text: "Stamp Duty Exemption" },
        { value: "SHARE_CAPITAL_EXEMPTION", text: "Share Capital Exemption" }
      ]
    }
  },
  data() {
    return {
      loading: false,
      form: {
        trackingNo: "",
        rationale: "",
        type: null,
        supportingDocument: null
      },
      errors: {
        rationale: null,
        type: null,
        supportingDocument: null
      }
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.trackingNo &&
        this.form.rationale &&
        this.form.rationale.length <= 255 &&
        this.form.type &&
        this.form.supportingDocument
      );
    }
  },
  mounted() {
    this.form.trackingNo = this.trackingNumber;
  },
  methods: {
    ...mapActions(["submitStampDutyExemption"]),
    
    validateForm() {
      this.errors = {
        rationale: null,
        type: null,
        supportingDocument: null
      };

      let isValid = true;

      if (!this.form.rationale || this.form.rationale.trim() === "") {
        this.errors.rationale = "Please provide a rationale";
        isValid = false;
      } else if (this.form.rationale.length > 255) {
        this.errors.rationale = "Rationale should be at most 255 characters";
        isValid = false;
      }

      if (!this.form.type) {
        this.errors.type = "Please select type of application";
        isValid = false;
      }

      if (!this.form.supportingDocument) {
        this.errors.supportingDocument = "Please provide supporting document";
        isValid = false;
      }

      return isValid;
    },

    async submitExemption() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const formData = new FormData();
        formData.append("trackingNo", this.form.trackingNo);
        formData.append("rationale", this.form.rationale);
        formData.append("type", this.form.type);
        formData.append("supportingDocument", this.form.supportingDocument);

        await this.submitStampDutyExemption(formData);
        
        this.successToast("Success", "Stamp duty exemption submitted successfully");
        this.$emit("exemption-submitted");
        this.resetForm();
      } catch (error) {
        this.errorToast("Error", error.response?.data?.message || "Failed to submit exemption");
      } finally {
        this.loading = false;
      }
    },

    cancel() {
      this.resetForm();
      this.$emit("exemption-cancelled");
    },

    resetForm() {
      this.form = {
        trackingNo: this.trackingNumber,
        rationale: "",
        type: null,
        supportingDocument: null
      };
      this.errors = {
        rationale: null,
        type: null,
        supportingDocument: null
      };
    }
  }
};
</script>

<style scoped>
.form-control:focus {
  border-color: #007ac5;
  box-shadow: 0 0 0 0.2rem rgba(0, 122, 197, 0.25);
}

.btn-primary {
  background-color: #007ac5;
  border-color: #007ac5;
}

.btn-primary:hover {
  background-color: #005a8b;
  border-color: #005a8b;
}
</style>
